import type { CSSProperties, ReactNode } from 'react'
import { useT } from '@/i18n/useT'

/**
 * Structural pieces of the redesign. The mockup lays every screen out as a
 * 24px-padded column with a 20px gap, a 30px page title, and content in
 * bordered 12px-radius cards — these components are that vocabulary.
 */

export function Page({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, ...style }}
    >
      {children}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '-0.4px',
            lineHeight: 1.15,
          }}
        >
          {title}
        </h1>
        {subtitle != null && (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{subtitle}</div>
        )}
      </div>
      {actions != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  )
}

export function Card({
  children,
  padding = 18,
  style,
  className,
  onClick,
}: {
  children: ReactNode
  padding?: number | string
  style?: CSSProperties
  className?: string
  onClick?: () => void
}) {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding,
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** The paper strip that holds search and filters above a list. */
export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: 'var(--paper)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      {children}
    </div>
  )
}

export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--faint)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function CardTitle({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0 }}>{children}</div>
      {note != null && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{note}</div>}
    </div>
  )
}

export function HRule({ style }: { style?: CSSProperties }) {
  return <div style={{ height: 1, background: 'var(--line)', ...style }} />
}

export function Spacer() {
  return <div style={{ flex: 1 }} />
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div
      style={{
        border: '1px dashed var(--line)',
        borderRadius: 12,
        padding: 48,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      {hint && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>{hint}</div>}
    </div>
  )
}

/**
 * A collapsible summary bar — the mockup's "Kennzahlen · … · Aufklappen ▼"
 * pattern, used to keep secondary detail out of the way until asked for.
 */
export function CollapseBar({
  label,
  summary,
  open,
  onToggle,
}: {
  label: ReactNode
  summary?: ReactNode
  open: boolean
  onToggle: () => void
}) {
  const { t } = useT()
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: 'var(--paper)',
        padding: '12px 16px',
        cursor: 'pointer',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{label}</div>
      {summary != null && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{summary}</div>}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
        {open ? `${t('Collapse')} ▲` : `${t('Expand')} ▼`}
      </div>
    </div>
  )
}
